import React, { useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import Commons from '../../../../Services/Common/Common';
import GridFunctions from '../../../../Services/agGrid/ag-Grid-functions';
import { showSnackbar } from '../../../../Reducer/SnackbarReducer';
import useCheckDuplicate from '../../../../hooks/useCheckDuplicate';
import useDelete from '../../../../hooks/useDeleteN';
import { useSave } from '../../../../hooks/useSaveN';
import { MEN_BASIC } from './index';
import {
  useDelete1,
  useDelete2,
  useSave1,
  useSave2,
  useSearch1,
  useSearch2,
  useSearchCode,
  useSearchMenuUp,
} from './MEN_BASIC_QUERY';
import { MEN_BASIC_Schema } from './MEN_BASIC_Schema';

const SCREEN_MENU_ID = 'Menu';

function MEN_BASICF() {
  const dispatch = useDispatch();
  const { userInfo } = useSelector((state) => state.BaseInfo);
  const { checkDuplicate } = useCheckDuplicate();

  const gridRef1 = useRef();
  const gridRef2 = useRef();
  const currentRowIndexRef1 = useRef(null);
  const selectedRowNosRef1 = useRef(new Set());
  const selectedRowNosRef2 = useRef(new Set());
  const lastCheckedIndexRef1 = useRef(null);
  const lastCheckedIndexRef2 = useRef(null);

  const [codeList, setCodeList] = useState([]);
  const [menuUpList, setMenuUpList] = useState([]);
  const [rowData1, setRowData1] = useState([]);
  const [rowData2, setRowData2] = useState([]);
  const [selectedRowData1, setSelectedRowData1] = useState([]);
  const [selectedRowData2, setSelectedRowData2] = useState([]);
  const [changedRowData1, setChangedRowData1] = useState([]);
  const [changedRowData2, setChangedRowData2] = useState([]);
  const [deletedRowData1, setDeletedRowData1] = useState([]);
  const [deletedRowData2, setDeletedRowData2] = useState([]);

  useSearchCode({ onSuccess: setCodeList });
  useSearchMenuUp({ onSuccess: (rows) => setMenuUpList(rows || []) });
  const { refetch: SearchMenuList } = useSearch1({
    onSuccess: (rows) => {
      const list = rows || [];
      if (!list.length) {
        setRowData1([]);
        return;
      }
      setRowData1(Commons.InsertNumber(list));
    },
  });
  const { runAsync: SearchProgramList } = useSearch2({
    onSuccess: (rows) => {
      const list = rows || [];
      if (!list.length) {
        setRowData2([]);
        return;
      }
      setRowData2(Commons.InsertNumber(list));
    },
  });

  const saveMenuMutation = useSave1({ codeList, userInfo });
  const saveProgramMutation = useSave2({
    menuId: selectedRowData1?.[0]?.menuId ?? '',
    userInfo,
  });
  const deleteMenuMutation = useDelete1();
  const deleteProgramMutation = useDelete2();

  const deleteHandler1 = useDelete({
    gridRef: gridRef1,
    deleteMutation: deleteMenuMutation,
    updateDeleted: setDeletedRowData1,
    updateRowData: setRowData1,
    deletedRowData: deletedRowData1,
    rowData: rowData1,
    setChangedRowData: setChangedRowData1,
  });

  const deleteHandler2 = useDelete({
    gridRef: gridRef2,
    deleteMutation: deleteProgramMutation,
    updateDeleted: setDeletedRowData2,
    updateRowData: setRowData2,
    deletedRowData: deletedRowData2,
    rowData: rowData2,
    setChangedRowData: setChangedRowData2,
  });

  const SearchLogic1 = async () => {
    setRowData2([]);
    setSelectedRowData1([]);
    setSelectedRowData2([]);
    setChangedRowData1([]);
    setChangedRowData2([]);
    setDeletedRowData1([]);
    setDeletedRowData2([]);
    currentRowIndexRef1.current = null;
    gridRef1?.current?.api?.deselectAll();
    gridRef2?.current?.api?.deselectAll();
    await SearchMenuList();
  };

  const SearchLogic2 = async (menuRow) => {
    const menuId = menuRow?.menuId ?? menuRow ?? '';
    if (!menuId) {
      setRowData2([]);
      return;
    }
    setSelectedRowData2([]);
    setChangedRowData2([]);
    setDeletedRowData2([]);
    gridRef2?.current?.api?.deselectAll();
    await SearchProgramList({ menuId });
  };

  useEffect(() => {
    SearchLogic1();
  }, []);

  const saveMenu = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '메뉴정보',
    schema: { fields: MEN_BASIC_Schema.fieldsMenu },
    getChangedRows: () => changedRowData1.filter((r) => !r.deleteYn),
    getDeletedRows: () => deletedRowData1,
    requestDelete: deleteHandler1.DeleteLogic,
    requestSave: saveMenuMutation.runAsync,
    onSuccessRefresh: async () => {
      await SearchLogic1();
    },
  });

  const saveProgram = useSave({
    menuId: SCREEN_MENU_ID,
    requiredLabel: '프로그램정보',
    schema: { fields: MEN_BASIC_Schema.fieldsProgram },
    getChangedRows: () => changedRowData2.filter((r) => !r.deleteYn),
    getDeletedRows: () => deletedRowData2,
    requestDelete: deleteHandler2.DeleteLogic,
    requestSave: saveProgramMutation.runAsync,
    onSuccessRefresh: async () => {
      await SearchLogic2(selectedRowData1?.[0]);
    },
  });

  const SaveLogic1 = async () => {
    const changedRowsMenu = changedRowData1.filter((r) => !r.deleteYn);
    const hasMenuWork =
      changedRowsMenu.length > 0 || (deletedRowData1?.length ?? 0) > 0;
    const changedRowsProgram = changedRowData2.filter((r) => !r.deleteYn);
    const hasProgramWork =
      changedRowsProgram.length > 0 || (deletedRowData2?.length ?? 0) > 0;

    if (!hasMenuWork && !hasProgramWork) {
      dispatch(
        showSnackbar({
          message: '저장할 데이터가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (changedRowsMenu.length > 0) {
      const keyArray = [{ field: 'menuId', name: '메뉴ID' }];
      if (!checkDuplicate(rowData1, changedRowsMenu, keyArray)) return;
    }

    if (changedRowsProgram.length > 0) {
      const keyArray = [{ field: 'proId', name: '프로그램ID' }];
      if (!checkDuplicate(rowData2, changedRowsProgram, keyArray)) return;
    }

    if (hasMenuWork) {
      await saveMenu.run();
    }
    if (hasProgramWork && !selectedRowData1?.[0]?.menuId) {
      dispatch(
        showSnackbar({
          message: '선택된 메뉴정보가 없습니다.',
          type: 'warning',
        })
      );
      return;
    }

    if (hasProgramWork && selectedRowData1?.[0]?.menuId) {
      await saveProgram.run();
    }
  };

  const AddClick1 = () => {
    GridFunctions.addClick(gridRef1, setChangedRowData1);
    setRowData2([]);
    setSelectedRowData2([]);
    setChangedRowData2([]);
    setDeletedRowData2([]);
  };

  const AddClick2 = () => {
    if (!selectedRowData1?.length) {
      dispatch(
        showSnackbar({
          message: '메뉴정보 항목 선택 후 추가하세요.',
          type: 'warning',
        })
      );
      return;
    }
    GridFunctions.addClick(gridRef2, setChangedRowData2);
  };

  const DeleteClick1 = () => {
    const newRowExists = selectedRowData1.some((row) => row.newYn === 'Y');
    if (newRowExists) {
      dispatch(
        showSnackbar({
          message: '데이터 입력중입니다. 저장 후 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }
    deleteHandler1.MarkedForDeletion(selectedRowData1);
  };

  const DeleteClick2 = () => {
    const newRowExists = selectedRowData2.some((row) => row.newYn === 'Y');
    if (newRowExists) {
      dispatch(
        showSnackbar({
          message: '데이터 입력중입니다. 저장 후 시도하세요.',
          type: 'warning',
        })
      );
      return;
    }
    deleteHandler2.MarkedForDeletion(selectedRowData2);
  };

  const handleRowSelection1 = (rowData, rowIndex) => {
    currentRowIndexRef1.current = rowIndex;
    setSelectedRowData1([rowData]);
  };

  const handleRowSelection2 = (rowData) => {
    setSelectedRowData2([rowData]);
  };

  const OnCellClicked1 = (e) => {
    const clickedIndex = e.rowIndex;
    if (currentRowIndexRef1.current === clickedIndex) return;
    if (e.data?.newYn === 'Y') {
      setRowData2([]);
      setSelectedRowData2([]);
      setChangedRowData2([]);
      setDeletedRowData2([]);
    } else {
      SearchLogic2(e.data);
    }
    handleRowSelection1(e.data, clickedIndex);
  };

  const OnSelectionChanged1 = () => {
    const gridApi = gridRef1?.current?.api;
    const selectedNodes = gridApi?.getSelectedNodes() ?? [];
    const selectedData = selectedNodes.map((n) => n.data);
    selectedRowNosRef1.current = new Set(selectedData.map((r) => r.no));
    setSelectedRowData1(selectedData);
  };

  const OnSelectionChanged2 = () => {
    const gridApi = gridRef2?.current?.api;
    const selectedNodes = gridApi?.getSelectedNodes() ?? [];
    const selectedData = selectedNodes.map((n) => n.data);
    selectedRowNosRef2.current = new Set(selectedData.map((r) => r.no));
    setSelectedRowData2(selectedData);
  };

  return (
    <div className="wrap">
      <MEN_BASIC
        gridRef1={gridRef1}
        gridRef2={gridRef2}
        codeList={codeList}
        menuUpList={menuUpList}
        rowData1={rowData1}
        rowData2={rowData2}
        selectedRowData1={selectedRowData1}
        selectedRowData2={selectedRowData2}
        setSelectedRowData1={setSelectedRowData1}
        setSelectedRowData2={setSelectedRowData2}
        setChangedRowData1={setChangedRowData1}
        setChangedRowData2={setChangedRowData2}
        selectedRowNosRef1={selectedRowNosRef1}
        selectedRowNosRef2={selectedRowNosRef2}
        lastCheckedIndexRef1={lastCheckedIndexRef1}
        lastCheckedIndexRef2={lastCheckedIndexRef2}
        handleRowSelection1={handleRowSelection1}
        handleRowSelection2={handleRowSelection2}
        RestoreRow1={deleteHandler1.RestoreRow}
        RestoreRow2={deleteHandler2.RestoreRow}
        DeleteNewRow1={deleteHandler1.DeleteNewRow}
        DeleteNewRow2={deleteHandler2.DeleteNewRow}
        SearchLogic1={SearchLogic1}
        SaveLogic1={SaveLogic1}
        AddClick1={AddClick1}
        AddClick2={AddClick2}
        DeleteClick1={DeleteClick1}
        DeleteClick2={DeleteClick2}
        OnCellClicked1={OnCellClicked1}
        OnSelectionChanged1={OnSelectionChanged1}
        OnSelectionChanged2={OnSelectionChanged2}
      />
    </div>
  );
}

export default MEN_BASICF;
